import CloudDownload from '@mdi/svg/svg/cloud-download.svg?react';
import RefreshIcon from '@mdi/svg/svg/refresh.svg?react';
import {
  type IEvent,
  type VideoLinkItem,
} from '@solink/vd-data-layer/api-clients/platform/platform';
import { type EventId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import settings from '@solinkcorp/settings';
import { Button, LoadingSpinner, MDIIcon, Modal } from '@solinkcorp/solink-components';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import TimeFormatter from 'src/utils/TimeFormatter';
import './AIVerificationDebugModal.sass';
const RETRY_DELAY_MS = 10000;
const REQUEST_TIMEOUT_MS = 60000;
const MAX_POLL_ATTEMPTS = 12; // ~2min at 10s interval

interface DebugVideoApiResponse {
  status?: 'inProgress' | 'completed';
  message?: string;
  debugVideoUrl?: string;
}

interface FetchDebugVideoParams {
  triggerEventId: EventId;
  alarmEventId: EventId;
  host: string;
  signal: AbortSignal;
  token: string;
  /** When true, include in request body. Omit for polling requests. */
  force?: boolean;
}

async function fetchDebugVideo({
  triggerEventId,
  alarmEventId,
  host,
  signal,
  token,
  force,
}: FetchDebugVideoParams): Promise<DebugVideoApiResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  const abortHandler = () => {
    controller.abort();
  };
  signal.addEventListener('abort', abortHandler);

  const body: { triggerEventId: EventId; alarmEventId: EventId; force?: boolean } = {
    triggerEventId,
    alarmEventId,
  };
  if (force === true) {
    body.force = true;
  }

  try {
    const response = await fetch(`${host}/ai-verification-debug`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    signal.removeEventListener('abort', abortHandler);

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status} ${response.statusText}`);
    }

    return (await response.json()) as DebugVideoApiResponse;
  } catch (err) {
    clearTimeout(timeoutId);
    signal.removeEventListener('abort', abortHandler);
    throw err;
  }
}

type DebugVideoState =
  | { status: 'loading'; message?: string }
  | { status: 'ready'; debugVideoUrl: string }
  | { status: 'error'; message: string };

interface DebugVideoViewerProps {
  index: number;
  item: VideoLinkItem;
  alarmEventId: EventId;
}

function DebugVideoViewer({ index, item, alarmEventId }: DebugVideoViewerProps) {
  const locale = useLocale();
  const user = useUserSession();
  const transactionId = item.metadata?.transactionId;
  const [state, setState] = useState<DebugVideoState>({ status: 'loading' });
  const abortRef = useRef<AbortController | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchVideo = useCallback(
    async (forceFirstRequest: boolean) => {
      if (!transactionId) {
        setState({ status: 'error', message: locale.alarms.noVideoLinkItems });
        return;
      }

      setState({ status: 'loading', message: locale.alarms.debugDataGenerating });

      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;

      const poll = async (force: boolean, attempt = 1): Promise<void> => {
        try {
          if (attempt > MAX_POLL_ATTEMPTS) {
            setState({ status: 'error', message: locale.alarms.debugDataPollingTimeout });
            return;
          }

          const result = await fetchDebugVideo({
            triggerEventId: transactionId as EventId,
            alarmEventId,
            host: settings.aiVerificationDebugHost,
            signal,
            token: user.accessToken,
            force,
          });

          if (result.debugVideoUrl) {
            setState({ status: 'ready', debugVideoUrl: result.debugVideoUrl });
            return;
          }

          if (result.status === 'inProgress') {
            await new Promise<void>((resolve) => {
              retryTimeoutRef.current = setTimeout(resolve, RETRY_DELAY_MS);
            });
            return poll(false, attempt + 1); // Subsequent polls omit force
          }

          setState({
            status: 'error',
            message: result.message || locale.general.error,
          });
        } catch (err) {
          if ((err as Error).name === 'AbortError') return;

          const message = err instanceof Error ? err.message : String(err);
          setState({ status: 'error', message });
        }
      };

      poll(forceFirstRequest, 1);
    },
    [
      alarmEventId,
      locale.alarms.debugDataGenerating,
      locale.alarms.debugDataPollingTimeout,
      locale.alarms.noVideoLinkItems,
      locale.general.error,
      transactionId,
      user.accessToken,
    ],
  );

  useEffect(() => {
    fetchVideo(false);
    return () => {
      abortRef.current?.abort();
      if (retryTimeoutRef.current !== null) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [fetchVideo]);

  const handleSaveToDisk = useCallback(() => {
    if (state.status !== 'ready') return;

    const { debugVideoUrl } = state;
    const link = document.createElement('a');
    link.href = debugVideoUrl;
    link.download = `debug-video-${transactionId ?? 'unknown'}.mp4`;
    link.target = '_blank';
    link.rel = 'noreferrer';
    link.click();
  }, [state, transactionId]);

  // Format timestamp if available
  const timeFormat = TimeFormatter.getString({ seconds: true });
  const formattedTimestamp = item.timestamp ? moment(item.timestamp).format(timeFormat) : null;
  const title = item.title || locale.alarms.videoFallback.replace('__index__', String(index + 1));
  const displayTitle = formattedTimestamp ? `${title} - ${formattedTimestamp}` : title;

  if (state.status === 'loading') {
    return (
      <div className="ai-verification-debug-viewer ai-verification-debug-viewer--loading">
        <h4>{displayTitle}</h4>
        <LoadingSpinner />
        <span>{state.message ?? locale.alarms.debugDataGenerating}</span>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="ai-verification-debug-viewer ai-verification-debug-viewer--error">
        <h4>{displayTitle}</h4>
        <p>{state.message}</p>
        <Button
          icon={<MDIIcon icon={RefreshIcon} />}
          onClick={() => fetchVideo(true)}
          text={locale.general.retry}
        />
      </div>
    );
  }

  return (
    <div className="ai-verification-debug-viewer ai-verification-debug-viewer--ready">
      <h4>{displayTitle}</h4>
      <video controls src={state.debugVideoUrl} className="ai-verification-debug-video">
        {locale.alarms.videoTagNotSupported}
      </video>
      <Button
        icon={<MDIIcon icon={CloudDownload} />}
        onClick={handleSaveToDisk}
        text={locale.download.download}
      />
    </div>
  );
}

interface AIVerificationDebugModalProps {
  event: IEvent;
  isModalOpen: boolean;
  closeModal: () => void;
}

export default function AIVerificationDebugModal({
  event,
  isModalOpen,
  closeModal,
}: AIVerificationDebugModalProps) {
  const locale = useLocale();

  const itemsWithTransactionId = React.useMemo(
    () =>
      (event.videoLinkItems ?? [])
        .filter(
          (item): item is VideoLinkItem & { metadata: { transactionId: string } } =>
            !!item.metadata?.transactionId && typeof item.metadata.transactionId === 'string',
        )
        .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0)),
    [event.videoLinkItems],
  );

  if (!isModalOpen) return null;

  return (
    <Modal
      className="ai-verification-debug-modal"
      colorMode="dark"
      dataTestId="ai-verification-debug-modal"
      isOpen={isModalOpen}
      onClose={closeModal}
      title={locale.alarms.aiVerificationDebug}
      type="wide"
      actions={[{ isPrimary: false, text: locale.general.close, onClick: closeModal }]}
    >
      <div className="ai-verification-debug-modal__content">
        {itemsWithTransactionId.length === 0 ? (
          <p>{locale.alarms.noVideoLinkItems}</p>
        ) : (
          itemsWithTransactionId.map((item, index) => (
            <div
              key={`${item.metadata.transactionId}-${index}`}
              className="ai-verification-debug-modal__item"
            >
              <DebugVideoViewer index={index} item={item} alarmEventId={event.id} />
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
